/* eslint-disable max-lines */
import {
  Alert,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Modal,
  Stack,
  Stepper,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import {
  CopySimple,
  MagnifyingGlass,
  WarningCircle,
  X,
} from '@phosphor-icons/react';
import sortBy from 'lodash/sortBy';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  SCHEDULE_FREQUENCY,
  FREQUENCY_DAYS,
  DAYS_TO_FREQUENCY,
} from 'api/types/card-schedule.types';
import { permissionIds } from 'components/common/klueCard/partials/klue-card-visibility-groups/helpers';
import PermissionsStep from 'components/common/klueCard/partials/wizard-steps/PermissionsStep';
import RivalRow from 'components/common/klueCard/partials/wizard-steps/RivalRow';
import ScheduleStep from 'components/common/klueCard/partials/wizard-steps/ScheduleStep';
import useVisibilityGroups from 'hooks/use-visibility-groups/useVisibilityGroups';
import store from 'store';
import TRACKING_IDS from 'tracking-ids';

import type { ScheduleFrequency } from 'api/types/card-schedule.types';
import type { RivalSelection } from 'components/common/klueCard/partials/wizard-steps/RivalRow';
import type { ChangeEvent, ReactNode } from 'react';
import type { Dispatch } from 'store/store.types';

type CopyOverrides = {
  isDraft: boolean;
  allAccess: boolean;
  visibilityGroups?: number[];
  frequencyDays: number | null;
  researchMode: boolean;
  prompt?: string;
};

type CopyToBoardsModalProps = {
  onClose: () => void;
  onCopy: (
    targets: {
      rivalId: number;
      boardId: number;
      rivalName: string;
      existingCardId?: number;
    }[],
    overrides?: CopyOverrides,
  ) => Promise<{ rivalId: number; cardId?: number }[]>;
  isPromptRivalSpecific: boolean;
  excludeRivalId?: number;
  sourcePrompt: string;
  sourceFrequencyDays: number | null;
  sourceResearchMode: boolean;
  sourceIsDraft: boolean;
  sourceAllAccess: boolean;
  sourceVisibilityGroups: number[];
};

const selectRivals = store.select(({ rivals: { all } }) => ({
  rivals: all,
}));

const RIVAL_ROW_TRACKING_IDS = {
  boardSelect: TRACKING_IDS.global.card.copyToBoards.boardSelect,
  laneSelect: TRACKING_IDS.global.card.copyToBoards.laneSelect,
  addLane: TRACKING_IDS.global.card.copyToBoards.addLane,
};

export default function CopyToBoardsModal({
  onClose,
  onCopy,
  isPromptRivalSpecific,
  excludeRivalId,
  sourcePrompt,
  sourceFrequencyDays,
  sourceResearchMode,
  sourceIsDraft,
  sourceAllAccess,
  sourceVisibilityGroups,
}: CopyToBoardsModalProps): ReactNode {
  const { t } = useTranslation(['Card', 'Common', 'Dashboard']);
  const dispatch = useDispatch<Dispatch>();
  const { rivals: allRivals } = useSelector(selectRivals);
  const sourceRivalName = useMemo(
    () => allRivals.find((r) => r.id === excludeRivalId)?.name ?? '',
    [allRivals, excludeRivalId],
  );
  const promptContainsRivalName =
    !!sourceRivalName && sourcePrompt.includes(sourceRivalName);
  const rivals = useMemo(
    () =>
      excludeRivalId
        ? allRivals.filter((r) => r.id !== excludeRivalId)
        : allRivals,
    [allRivals, excludeRivalId],
  );

  // Wizard state
  const [step, setStep] = useState(0);
  const [editedPrompt, setEditedPrompt] = useState(sourcePrompt);
  const [promptEdited, setPromptEdited] = useState(false);
  const [selections, setSelections] = useState<Record<number, RivalSelection>>(
    {},
  );
  const [isCopying, setIsCopying] = useState(false);
  const [search, setSearch] = useState('');
  const [rivalAlertDismissed, setRivalAlertDismissed] = useState(false);
  const [scheduleAlertDismissed, setScheduleAlertDismissed] = useState(false);

  // Defer rival list rendering to avoid blocking the step transition
  const [rivalsReady, setRivalsReady] = useState(false);

  useEffect(() => {
    if (step === 3) {
      setRivalAlertDismissed(false);
      setScheduleAlertDismissed(false);
      const id = requestAnimationFrame(() => setRivalsReady(true));
      return () => cancelAnimationFrame(id);
    }
    setRivalsReady(false);
  }, [step]);

  // Permissions state (via Redux)
  const {
    visibleTo,
    selectedGroups,
    deselectAllGroups,
    setVisibleTo,
    setSelectGroups,
  } = useVisibilityGroups();

  const getDefaultVisibleTo = (): number => {
    if (sourceIsDraft) return permissionIds.curatorsOnly;
    if (sourceAllAccess) return permissionIds.everyone;
    return permissionIds.curatorsAnd;
  };
  const defaultVisibleTo = getDefaultVisibleTo();

  // Initialise permissions state once on mount. sourceVisibilityGroups is an
  // inline-mapped array from the parent so its reference changes every render;
  // including it in deps would create an infinite fetch loop.
  useEffect(() => {
    deselectAllGroups();
    setVisibleTo(defaultVisibleTo);
    if (
      defaultVisibleTo === permissionIds.curatorsAnd &&
      sourceVisibilityGroups.length > 0
    ) {
      setSelectGroups(sourceVisibilityGroups);
    }
    dispatch.visibilityGroups.fetchVisibilityGroupsFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Schedule state
  const defaultFrequency: ScheduleFrequency = sourceFrequencyDays
    ? DAYS_TO_FREQUENCY[sourceFrequencyDays] ?? SCHEDULE_FREQUENCY.MONTHLY
    : SCHEDULE_FREQUENCY.UNSCHEDULED;
  const [frequency, setFrequency] =
    useState<ScheduleFrequency>(defaultFrequency);
  const isUnscheduled = frequency === SCHEDULE_FREQUENCY.UNSCHEDULED;

  // Defer heavy content rendering to avoid blocking the modal open animation
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setIsReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Rivals state
  const sortedRivals = useMemo(
    () => sortBy(rivals, [(r) => r.name.toLowerCase()]),
    [rivals],
  );

  const filteredRivals = useMemo(() => {
    if (!search.trim()) return sortedRivals;
    const lower = search.toLowerCase();
    return sortedRivals.filter((r) => r.name.toLowerCase().includes(lower));
  }, [sortedRivals, search]);

  const toggleRival = useCallback((rivalId: number) => {
    setSelections((prev) => {
      const current = prev[rivalId] ?? { checked: false };
      return {
        ...prev,
        [rivalId]: {
          ...current,
          checked: !current.checked,
          boardId: !current.checked ? current.boardId : undefined,
        },
      };
    });
  }, []);

  const updateBoardId = useCallback(
    (rivalId: number, boardId: number | undefined) => {
      setSelections((prev) => ({
        ...prev,
        [rivalId]: { ...prev[rivalId], checked: true, boardId },
      }));
    },
    [],
  );

  const validTargets = useMemo(
    () =>
      Object.entries(selections)
        .filter(
          (entry): entry is [string, RivalSelection & { boardId: number }] =>
            entry[1].checked &&
            entry[1].boardId !== undefined &&
            entry[1].status !== 'success',
        )
        .map(([rivalId, sel]) => ({
          rivalId: Number(rivalId),
          boardId: sel.boardId,
          rivalName: rivals.find((r) => r.id === Number(rivalId))?.name ?? '',
          existingCardId: sel.cardId,
        })),
    [selections, rivals],
  );

  const checkedCount = useMemo(
    () =>
      Object.values(selections).filter(
        (s) => s.checked && s.status !== 'success',
      ).length,
    [selections],
  );

  const isLastStepValid =
    validTargets.length > 0 && validTargets.length === checkedCount;

  const handleCopy = useCallback(async () => {
    if (!isLastStepValid) return;

    const isDraft = visibleTo === permissionIds.curatorsOnly;
    const allAccess = visibleTo === permissionIds.everyone;
    const frequencyDays = isUnscheduled
      ? null
      : FREQUENCY_DAYS[frequency as Exclude<ScheduleFrequency, 'unscheduled'>];

    setIsCopying(true);
    try {
      const failures = await onCopy(validTargets, {
        isDraft,
        allAccess: !isDraft && allAccess,
        visibilityGroups:
          isDraft || allAccess ? undefined : Array.from(selectedGroups),
        frequencyDays,
        researchMode: sourceResearchMode,
        prompt: editedPrompt,
      });

      if (failures.length === 0) {
        onClose();
      } else {
        const failedMap = new Map(failures.map((f) => [f.rivalId, f.cardId]));
        setSelections((prev) => {
          const next = { ...prev };
          for (const [id, sel] of Object.entries(next)) {
            if (!sel.checked) continue;
            const numId = Number(id);
            next[numId] = {
              ...sel,
              status: failedMap.has(numId) ? 'error' : 'success',
              cardId: failedMap.get(numId) ?? sel.cardId,
            };
          }
          return next;
        });
      }
    } finally {
      setIsCopying(false);
    }
  }, [
    isLastStepValid,
    visibleTo,
    selectedGroups,
    isUnscheduled,
    frequency,
    sourceResearchMode,
    editedPrompt,
    validTargets,
    onCopy,
    onClose,
  ]);

  return (
    <Modal
      opened
      onClose={onClose}
      closeOnClickOutside={!isCopying}
      closeOnEscape={!isCopying}
      size={600}
      centered
      title={
        <Group gap="sm" align="center">
          <CopySimple />
          <Title order={2} size="h4">
            {t('Card:actions.copyToBoards.title')}
          </Title>
        </Group>
      }
      closeButtonProps={{
        icon: <X />,
        disabled: isCopying,
      }}
      styles={{
        header: {
          backgroundColor: 'var(--tokens-color-primary-main)',
          color: 'white',
        },
        title: { color: 'white' },
        close: {
          color: 'white',
          backgroundColor: 'transparent',
        },
        content: {
          height: '90vh',
          maxHeight: 700,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0,
        },
      }}
    >
      {!isReady ? (
        <Center p="lg" style={{ minHeight: 420 }}>
          <Loader />
        </Center>
      ) : (
        <Stack h="100%">
          <Box p="md">
            <Stepper active={step} size="sm">
              <Stepper.Step label={t('Card:actions.copyToBoards.step1Title')} />
              <Stepper.Step label={t('Card:actions.copyToBoards.step2Title')} />
              <Stepper.Step label={t('Card:actions.copyToBoards.step3Title')} />
              <Stepper.Step label={t('Card:actions.copyToBoards.step4Title')} />
            </Stepper>
          </Box>

          <Stack style={{ flex: 1, overflow: 'hidden', minHeight: 0 }} gap={0}>
            <Box p="md" pb={0}>
              <Text fw={600} fz="lg">
                {step === 0 && t('Card:actions.copyToBoards.editPromptLabel')}
                {step === 1 &&
                  t('Dashboard:askKlue.createCardModal.visibilityLabel')}
                {step === 2 && t('Card:actions.copyToBoards.frequencyLabel')}
                {step === 3 &&
                  t(
                    'Dashboard:askKlue.promptLibrary.advancedWizard.competitorsLabel',
                  )}
              </Text>
            </Box>
            <Box
              style={{
                flex: 1,
                overflowY: step === 0 ? 'hidden' : 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
              p="md"
            >
              {step === 0 && (
                <Stack gap="md" style={{ flex: 1 }}>
                  {promptContainsRivalName && !promptEdited && (
                    <Alert
                      icon={<WarningCircle size={20} />}
                      color="yellow"
                      withCloseButton
                      onClose={() => setPromptEdited(true)}
                      title={
                        <Trans
                          i18nKey="Card:actions.copyToBoards.editPromptRivalSwapHint"
                          values={{ rivalName: sourceRivalName }}
                          components={{ bold: <Text span fw={700} /> }}
                        />
                      }
                    />
                  )}
                  <Textarea
                    value={editedPrompt}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                      setEditedPrompt(e.target.value)
                    }
                    styles={{
                      root: {
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                      },
                      wrapper: { flex: 1 },
                      input: { height: '100%' },
                    }}
                  />
                </Stack>
              )}

              <Box display={step === 1 ? undefined : 'none'}>
                <PermissionsStep visibleToDefault={defaultVisibleTo} />
              </Box>

              {step === 2 && (
                <ScheduleStep
                  frequency={frequency}
                  onFrequencyChange={setFrequency}
                />
              )}

              {step === 3 && (
                <Stack gap="md">
                  <TextInput
                    placeholder={t(
                      'Card:actions.copyToBoards.searchPlaceholder',
                    )}
                    leftSection={<MagnifyingGlass size={16} />}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                  />

                  {!rivalAlertDismissed && !isPromptRivalSpecific && (
                    <Alert
                      icon={<WarningCircle size={20} />}
                      color="red"
                      withCloseButton
                      onClose={() => setRivalAlertDismissed(true)}
                      title={t(
                        'Card:actions.copyToBoards.promptNotRivalSpecific',
                      )}
                    />
                  )}

                  {!scheduleAlertDismissed && isUnscheduled && (
                    <Alert
                      icon={<WarningCircle size={20} />}
                      color="yellow"
                      withCloseButton
                      onClose={() => setScheduleAlertDismissed(true)}
                      title={t('Card:actions.copyToBoards.unscheduledWarning')}
                    />
                  )}

                  {!rivalsReady ? (
                    <Center p="lg">
                      <Loader size="sm" />
                    </Center>
                  ) : (
                    <Stack gap={0}>
                      {filteredRivals.map((rival, index) => (
                        <Stack key={rival.id} gap={0}>
                          {index > 0 && <Divider />}
                          <Stack gap={4} py="sm">
                            <RivalRow
                              rival={rival}
                              selection={
                                selections[rival.id] ?? { checked: false }
                              }
                              onToggle={() => toggleRival(rival.id)}
                              onBoardChange={(boardId) =>
                                updateBoardId(rival.id, boardId)
                              }
                              trackingIds={RIVAL_ROW_TRACKING_IDS}
                            />
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Stack>
              )}
            </Box>
          </Stack>

          <Box p="md" pt={0}>
            <Group justify="space-between">
              <Button
                variant="subtle"
                color="gray"
                onClick={onClose}
                disabled={isCopying}
                data-tracking-id={TRACKING_IDS.global.card.copyToBoards.cancel}
              >
                {t('Common:actions.cancel')}
              </Button>
              <Group gap="sm">
                {step > 0 && (
                  <Button
                    variant="subtle"
                    color="gray"
                    onClick={() => setStep((s) => s - 1)}
                    disabled={isCopying}
                  >
                    {t('Common:back')}
                  </Button>
                )}
                {step < 3 && (
                  <Button
                    variant="filled"
                    onClick={() => setStep((s) => s + 1)}
                  >
                    {t('Common:next')}
                  </Button>
                )}
                {step === 3 && (
                  <Button
                    variant="filled"
                    onClick={handleCopy}
                    loading={isCopying}
                    disabled={!isLastStepValid}
                    data-tracking-id={
                      TRACKING_IDS.global.card.copyToBoards.copy
                    }
                  >
                    {t('Card:actions.copyToBoards.copy')}
                  </Button>
                )}
              </Group>
            </Group>
          </Box>
        </Stack>
      )}
    </Modal>
  );
}
